import { clientSDK } from "@istari-frontend/api-client";
import { ArchiveStatus, type Job, type JobStatusName, type OptionalTotalPageJob } from "@istari/istari-client";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import type {
  AdaptiveJobInfiniteRefetchInterval,
  AdaptiveJobPageRefetchInterval,
  AdaptiveJobRefetchInterval,
} from "~/jobs/data-source/job-polling";

export type JobsByModelFilter = {
  modelId: string | null;
  allUsers?: boolean;
  page?: number;
  size?: number;
  state?: "active" | "archived" | null;
  sort?: string | null;
  statusName?: JobStatusName | null;
  name?: string | null;
};

const ALL_JOBS_INFINITE_PAGE_SIZE = 100;

const normaliseFilter = (filter: JobsByModelFilter) => ({
  modelId: filter.modelId ?? null,
  statusName: filter.statusName ?? null,
  allUsers: !!filter.allUsers,
  page: filter.page ?? 1,
  size: filter.size ?? 100,
  state: filter.state ?? "active",
  sort: filter.sort ?? "-created",
  name: filter.name ?? null,
});

export const jobsQueryKeys = {
  all: ["jobs"] as const,
  byId: (jobId: string) => ["job", jobId] as const,
  byFilter: (filter: JobsByModelFilter) => ["jobs", normaliseFilter(filter)] as const,
  byModelInfinite: (modelId: string, archiveStatus: ArchiveStatus) =>
    ["jobs", "by-model", modelId, archiveStatus, "infinite"] as const,
};

export const jobsQueryOptions = {
  byId: (jobId: string, opts?: { refetchInterval?: number | false | AdaptiveJobRefetchInterval; enabled?: boolean }) =>
    queryOptions<Job>({
      queryKey: jobsQueryKeys.byId(jobId),
      queryFn: () => clientSDK.getJob(jobId),
      enabled: (opts?.enabled ?? true) && !!jobId,
      refetchInterval: opts?.refetchInterval,
    }),
  byModel: (
    filter: JobsByModelFilter,
    opts?: { refetchInterval?: number | false | AdaptiveJobPageRefetchInterval; enabled?: boolean },
  ) => {
    const normalised = normaliseFilter(filter);
    return queryOptions<OptionalTotalPageJob>({
      queryKey: jobsQueryKeys.byFilter(filter),
      queryFn: () =>
        clientSDK.listJobs(
          normalised.modelId,
          normalised.statusName,
          normalised.allUsers,
          null,
          normalised.name,
          normalised.page,
          normalised.size,
          normalised.state,
          normalised.sort,
        ),
      enabled: opts?.enabled ?? true,
      refetchInterval: opts?.refetchInterval,
      staleTime: 0,
    });
  },
  byModelInfinite: (
    modelId: string,
    opts?: {
      enabled?: boolean;
      refetchInterval?: number | false | AdaptiveJobInfiniteRefetchInterval;
      archiveStatus?: ArchiveStatus;
    },
  ) => {
    // Defaults to "active" so the polled live-status drain stays cheap. Consumers that resolve the
    // producing job for an artifact group (group headers) pass "all" — without it, artifacts whose
    // job has since been archived (e.g. recurring nightly containers) leave the group with
    // job=undefined, rendering an inert bare short-UUID header instead of clickable job info.
    const archiveStatus = opts?.archiveStatus ?? ArchiveStatus.Active;
    return infiniteQueryOptions({
      queryKey: jobsQueryKeys.byModelInfinite(modelId, archiveStatus),
      queryFn: ({ pageParam }) =>
        clientSDK.listJobs(
          modelId,
          null,
          undefined,
          null,
          null,
          pageParam,
          ALL_JOBS_INFINITE_PAGE_SIZE,
          archiveStatus,
          "-created",
        ),
      initialPageParam: 1,
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        if (!lastPage?.total || lastPageParam * ALL_JOBS_INFINITE_PAGE_SIZE >= lastPage.total) return undefined;
        return lastPageParam + 1;
      },
      enabled: (opts?.enabled ?? true) && !!modelId,
      refetchInterval: opts?.refetchInterval,
      staleTime: 5 * 60 * 1000,
    });
  },
};
